import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import type { WorkspaceOperations } from './workspaceDashboard.model';
import { OPERATIONAL_QUEUES } from './operationalState';

const WorkspaceDashboardBoard: React.FC<{ ops: WorkspaceOperations }> = ({ ops }) => {
  return (
    <Stack gap={1.5}>
      <Box>
        <Typography level="title-lg">Status board</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          Same operational queues as Today. Lanes are derived from project facts — cards move on their own
          as quotes, invoices, orders and bookings change.
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(240px, 1fr)', md: `repeat(${OPERATIONAL_QUEUES.length}, minmax(190px, 1fr))` },
            gap: 1,
            minWidth: { md: 1000 },
          }}
        >
          {OPERATIONAL_QUEUES.map((lane) => {
            const cards = ops.board.filter((card) => card.queue === lane.id);
            const flagged = cards.filter((card) => card.flag).length;
            return (
              <Box
                key={lane.id}
                sx={{
                  bgcolor: 'background.level1',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'sm',
                  p: 1,
                  minHeight: 220,
                }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography level="title-sm">{lane.label}</Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {cards.length}{flagged ? ` · ${flagged} flagged` : ''}
                  </Typography>
                </Stack>
                <Stack gap={1}>
                  {cards.map((card) => (
                    card.flag || card.exception ? (
                      <Card
                        key={card.projectId}
                        variant={card.exception ? 'soft' : 'outlined'}
                        color={card.exception ? 'danger' : 'neutral'}
                        size="sm"
                      >
                        <CardContent>
                          <Typography
                            level="body-sm"
                            fontWeight="lg"
                            component={Link}
                            to={card.href}
                            sx={{ textDecoration: 'none' }}
                          >
                            {card.workspace}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                            {card.customer}
                          </Typography>
                          <Typography level="body-xs" sx={{ mt: 0.5 }}>{card.stateLabel}</Typography>
                          {card.note && card.note !== card.stateLabel && (
                            <Typography level="body-xs" fontWeight="lg" sx={{ mt: 0.25 }}>
                              {card.flag === 'office' ? 'Office · ' : 'Customer · '}{card.note}
                            </Typography>
                          )}
                          {card.flags.map((flag) => (
                            <Chip key={flag} size="sm" variant="outlined" color="warning" sx={{ mt: 0.5 }}>{flag}</Chip>
                          ))}
                        </CardContent>
                      </Card>
                    ) : (
                      <Box key={card.projectId} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}>
                        <Typography
                          level="body-sm"
                          fontWeight="md"
                          component={Link}
                          to={card.href}
                          sx={{ textDecoration: 'none' }}
                        >
                          {card.workspace}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {card.customer}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                          {card.stateLabel}
                        </Typography>
                        {card.flags.map((flag) => (
                          <Chip key={flag} size="sm" variant="outlined" color="warning" sx={{ mt: 0.5 }}>{flag}</Chip>
                        ))}
                      </Box>
                    )
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
};

export default WorkspaceDashboardBoard;
